import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://v0265h6199.execute-api.us-east-1.amazonaws.com/dev';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('session_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Auto-logout on expired/invalid token
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token is expired or invalid — clear session and send them to the
      // real sign-in screen with a reason, not a silent drop on the
      // marketing homepage (item 20). A hard reload, not a router navigate —
      // this fires from an axios interceptor outside any component, so
      // ?reason=expired has to be a query param (survives the reload);
      // router state would not.
      localStorage.removeItem('reviewflow_user');
      localStorage.removeItem('session_token');
      window.location.href = '/login?reason=expired';
    }
    return Promise.reject(error);
  }
);

// Public endpoints (no auth required)
export const getHealth = () => api.get('/api/health');
export const getInviteInfo = (code) => api.get(`/api/teams/invite/${encodeURIComponent(code)}`);

// Protected endpoints (session token required)
export const getPrs = (params = {}) => api.get('/api/prs', { params });
export const getUserMe = () => api.get('/api/users/me');
export const updateUserProfile = (fields) => api.patch('/api/users/me', fields);
// Irreversible. Deletes the team too when the caller is its only member.
export const deleteAccount = () => api.delete('/api/users/me');
export const getAnalytics = (params = {}) => api.get('/api/analytics', { params });

// GitHub App — sign in.
// GitHub Apps take no `scope` parameter; permissions come from the App's own
// configuration rather than the authorize URL.
//
// `state` is optional and, when passed, carries an invite code — GitHub
// echoes it back verbatim on the callback, letting auth-callback verify real
// GitHub org access before joining a team (item 15). Only JoinPage passes
// this; plain sign-in (Login.js, MarketingNav.js) never does, so an ordinary
// login can never trigger a join, regardless of what's in sessionStorage.
export const getGitHubAuthUrl = (state) => {
    const clientId = process.env.REACT_APP_GITHUB_CLIENT_ID;
    const base = `https://github.com/login/oauth/authorize?client_id=${clientId}`;
    return state ? `${base}&state=${encodeURIComponent(state)}` : base;
}

// GitHub App — install. This is how repos get connected now: GitHub's own
// screen picks the org and the repo selection, so there is nothing for us to
// build a picker for. With "Request user authorization during installation"
// enabled, it round-trips through OAuth and lands on our callback with both
// `code` and `installation_id`.
export const getGitHubInstallUrl = () => {
    const slug = process.env.REACT_APP_GITHUB_APP_SLUG;
    return `https://github.com/apps/${slug}/installations/new`;
}

// Where a team changes which repos the App can see — including removing one,
// which is what "disconnect" is now.
export const getInstallationSettingsUrl = (installationId) =>
    `https://github.com/settings/installations/${installationId}`;

// Team management
export const getMyTeam = () => api.get('/api/teams/me');
export const regenerateInviteCode = () =>
    api.post('/api/teams/invite-code');
export const setMemberRole = (userId, role) =>
    api.post('/api/teams/members/role', { user_id: userId, role });
export const removeTeamMember = (userId) =>
    api.post('/api/teams/members/remove', { user_id: userId });
export const leaveTeam = () => api.post('/api/teams/leave');
// Only the last remaining member may call it. Distinct from deleteAccount():
// this schedules the team for deletion and keeps the profile, where account
// deletion schedules both. Returns a fresh JWT with team_id cleared, same as
// leaveTeam.
//
// NOT irreversible any more — it soft-deletes with a 30-day window, and
// restoreTeam() below undoes it. The confirmation copy says so.
export const deleteTeam = () => api.delete('/api/teams/me');

// Cancels a pending team deletion inside the 30-day window.
//
// Deliberately an explicit action rather than something sign-in does for you:
// restoring a team also un-cancels its Stripe subscription, and reversing a
// billing decision on someone's behalf needs a click. (A pending ACCOUNT, by
// contrast, is restored automatically on sign-in — see auth-callback.)
//
// 410 means the window has passed; 404 means there is nothing pending.
export const restoreTeam = () => api.post('/api/teams/me/restore');

// Billing (item 9) — Checkout and the Billing Portal are both hosted-page
// redirects (window.location.href = res.data.data.checkout_url / portal_url),
// same pattern as getGitHubInstallUrl()'s usage. No Stripe.js dependency.
export const createBillingCheckoutSession = (interval = 'monthly') =>
    api.post('/api/teams/billing/checkout', { interval });
export const createBillingPortalSession = () =>
    api.post('/api/teams/billing/portal');
export const confirmPlanSelection = (repositoryFullNames, memberUserIds) =>
    api.post('/api/teams/plan-select', {
        repository_full_names: repositoryFullNames,
        member_user_ids: memberUserIds,
    });

export default api;
